import { RSAA } from 'redux-api-middleware'
import * as R from 'ramda'
import {
  getApiPath,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'

import { initialState, types } from 'store/Applicant'
import { cleanTempApplicant, siftApplicants } from 'utils/applicants'

/**
 * Fetches a new customized package with specific values. The applicant that's
 * passed in should have their quote model updated to the values we want to
 * estimate on. This endpoint returns a single quote model that we use to
 * replace the `customizedPackage` for the given applicant.
 *
 * @param {obj} applicant The applicant we want to get the estimate for
 * @param {obj} values The values that we want to get a estimate for
 * @param {str} funnelType The type of flow we are in (for reporting)
 */
export const customizePackage = (applicant, values, funnelType) => {
  if (R.either(R.isNil, R.isEmpty)(applicant)) {
    throw new Error('Cannot fetch estimate without an applicant')
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        `acct`,
        `pub/application/estimate${makeQueryParams({
          urlSlug: webStorage.getItem('ptnr'),
          funnelType,
        })}`,
      ),
      method: 'POST',
      headers: {
        'x-api-key': process.env.REACT_APP_X_API_KEY,
        token: 'IGNORE',
      },
      body: ({ Applicant }) => {
        const customPackage =
          Applicant.packages?.entities?.customizedPackage?.find(
            p => p.applicant === applicant.uuid,
          )

        return JSON.stringify({
          funnelType,
          applicant: cleanTempApplicant(applicant, { raiseStartDate: true }),
          packageInfo: {
            ...customPackage,
            ...values,
          },
        })
      },
      types: [
        types.CUSTOMIZE_PACKAGE_REQUEST,
        {
          type: types.CUSTOMIZE_PACKAGE_SUCCESS,
          meta: applicant,
        },
        types.CUSTOMIZE_PACKAGE_ERROR,
      ],
    },
  }
}

/**
 * Fetches a new customized package with specific values. The applicant that's
 * passed in should have their quote model updated to the values we want to
 * estimate on. This endpoint returns a single quote model that we use to
 * replace the `customizedPackage` for the given applicant.
 *
 * @param {obj} applicant The applicant we want to get the estimate for
 * @param {obj} values The values that we want to get a estimate for
 * @param {str} funnelType The type of flow we are in (for reporting)
 */
export const fetchEstimate = (applicant, packageInfo, funnelType) => {
  if (R.either(R.isNil, R.isEmpty)(applicant)) {
    throw new Error('Cannot fetch estimate without an applicant')
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        `acct`,
        `pub/application/estimate${makeQueryParams({
          urlSlug: webStorage.getItem('ptnr'),
          funnelType,
        })}`,
      ),
      method: 'POST',
      headers: {
        'x-api-key': process.env.REACT_APP_X_API_KEY,
        token: 'IGNORE',
      },
      body: JSON.stringify({
        funnelType,
        applicant: cleanTempApplicant(applicant, { raiseStartDate: true }),
        packageInfo,
      }),
      types: [
        types.FETCH_ESTIMATE_REQUEST,
        {
          type: types.FETCH_ESTIMATE_SUCCESS,
          meta: { applicantUuid: applicant.uuid },
        },
        types.FETCH_ESTIMATE_ERROR,
      ],
    },
  }
}

/**
 * Fetches an estimate for enabling/disabling a specific benefit for a specific
 * applicant.
 *
 * @param {str} benefit The benefit we want to get an estimate for
 * @param {obj} applicant The applicant who has the benefit
 * @param {obj} packageInfo An object with the benefit information
 * @param {str} funnelType Used by the backend for calcs.
 */
export const fetchBenefitEstimate = (
  benefit,
  applicant,
  packageInfo,
  funnelType,
) => ({
  [RSAA]: {
    endpoint: getApiPath(
      `acct`,
      `pub/application/estimate${makeQueryParams({
        urlSlug: webStorage.getItem('ptnr'),
        funnelType,
      })}`,
    ),
    method: 'POST',
    headers: {
      'x-api-key': process.env.REACT_APP_X_API_KEY,
      token: 'IGNORE',
    },
    body: JSON.stringify({
      funnelType,
      applicant: cleanTempApplicant(applicant, { raiseStartDate: true }),
      packageInfo,
    }),
    types: [
      {
        type: types.FETCH_BENEFIT_ESTIMATE_REQUEST,
        meta: { benefit, applicant },
      },
      {
        type: types.FETCH_BENEFIT_ESTIMATE_SUCCESS,
        meta: { benefit, applicant },
      },
      {
        type: types.FETCH_BENEFIT_ESTIMATE_ERROR,
        meta: { benefit, applicant },
      },
    ],
  },
})

/**
 * Resets the benefit to the current quote so that if the user changes it
 * without saving, it'll be the same value as before they changed it.
 *
 * @param {obj} quote The current quote to reset to
 * @param {str} benefit The benefit we want to reset
 * @param {str} uuid The applicant uuid we want to reset it for
 */
export const resetBenefitEstimate = (quote, benefit, uuid) => ({
  type: types.RESET_BENEFIT_ESTIMATE,
  payload: quote,
  meta: { benefit, uuid },
})

/**
 * Completely deletes an estimate for a member, useful when we want to reset a
 * benefit's temp_ estimate. Usually followed by a fetchBenefitEstimate
 *
 * @param {str} benefit The benefit we want to reset
 * @param {str} uuid The applicant uuid we want to reset it for
 */
export const clearBenefitEstimate = (benefit, uuid) => ({
  type: types.CLEAR_BENEFIT_ESTIMATE,
  payload: { benefit, uuid },
})

/**
 * Fetches the estimated costs for reinstating a suspended member.
 *
 * @param {str} uuid The UUID of the member we want to reinstate
 */
export const fetchReinstateEstimate = uuid => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `members/subscription/${uuid}/reinstate-estimate`,
    ),
    method: 'POST',
    body: JSON.stringify({ uuid }),
    types: [
      types.FETCH_REINSTATE_ESTIMATE_REQUEST,
      {
        type: types.FETCH_REINSTATE_ESTIMATE_SUCCESS,
        meta: { uuid },
      },
      types.FETCH_REINSTATE_ESTIMATE_ERROR,
    ],
  },
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.CUSTOMIZE_PACKAGE_REQUEST:
      return {
        ...state,
        packages: {
          ...state.packages,
          busy: 'custom',
          error: '',
        },
      }

    case types.CUSTOMIZE_PACKAGE_SUCCESS: {
      const packageIndex = state.packages.entities.customizedPackage.findIndex(
        p => p.applicant === meta.uuid,
      )
      const updatedPackage = {
        ...state.packages.entities.customizedPackage[packageIndex],
        applicant: meta.uuid,
        ...payload,
      }
      const updatedPackages = [...state.packages.entities.customizedPackage]
      updatedPackages[packageIndex] = updatedPackage
      return {
        ...state,
        packages: {
          ...state.packages,
          busy: null,
          entities: {
            ...state.packages.entities,
            customizedPackage: updatedPackages,
          },
        },
      }
    }

    case types.CUSTOMIZE_PACKAGE_ERROR:
      return {
        ...state,
        packages: {
          ...state.packages,
          busy: null,
          error: payload.response.message,
        },
      }

    case types.FETCH_ESTIMATE_REQUEST:
      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: true,
          error: '',
        },
      }

    case types.FETCH_ESTIMATE_SUCCESS: {
      const nextState = {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          entities: {
            ...state.applicants.entities,
            [meta.applicantUuid]: {
              ...state.applicants.entities[meta.applicantUuid],
              quote: payload,
            },
          },
        },
      }
      return siftApplicants(nextState)
    }

    case types.FETCH_ESTIMATE_ERROR:
      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          error: payload.response.message,
        },
      }

    case types.FETCH_BENEFIT_ESTIMATE_REQUEST: {
      const flagId = `${meta.benefit}:${meta.applicant.uuid}`
      return {
        ...state,
        estimates: {
          ...state.estimates,
          busy: {
            ...state.estimates.busy,
            [flagId]: true,
          },
          error: '',
        },
      }
    }

    case types.FETCH_BENEFIT_ESTIMATE_SUCCESS: {
      const flagIdSuccess = `${meta.benefit}:${meta.applicant.uuid}`
      const hasTempEstimate =
        state.estimates.entities?.[`temp_${meta.benefit}`] &&
        state.estimates.entities[`temp_${meta.benefit}`][meta.applicant.uuid]

      const newEntities = {
        ...state.estimates.entities,
        [meta.benefit]: {
          ...state.estimates.entities[meta.benefit],
          [meta.applicant.uuid]: payload,
        },
      }

      if (!hasTempEstimate) {
        newEntities[`temp_${meta.benefit}`] = {
          ...state.estimates.entities[`temp_${meta.benefit}`],
          [meta.applicant.uuid]: payload,
        }
      }

      return {
        ...state,
        estimates: {
          ...state.estimates,
          entities: newEntities,
          busy: {
            ...state.estimates.busy,
            [flagIdSuccess]: false,
          },
        },
      }
    }

    case types.FETCH_BENEFIT_ESTIMATE_ERROR: {
      const flagIdError = `${meta.benefit}:${meta.applicant.uuid}`
      const { [flagIdError]: _, ...restBusy } = state.estimates.busy
      return {
        ...state,
        estimates: {
          ...state.estimates,
          busy: restBusy,
          error: payload.response.message,
        },
      }
    }

    case types.RESET_BENEFIT_ESTIMATE:
      return {
        ...state,
        estimates: {
          ...state.estimates,
          entities: {
            ...state.estimates.entities,
            [`temp_${meta.benefit}`]: {
              ...state.estimates.entities[`temp_${meta.benefit}`],
              [meta.uuid]: payload,
            },
          },
        },
      }

    case types.CLEAR_BENEFIT_ESTIMATE: {
      const { benefit, uuid } = payload
      let updatedEntities = { ...state.estimates.entities }
      delete updatedEntities[benefit][uuid]
      delete updatedEntities[`temp_${benefit}`][uuid]
      return {
        ...state,
        estimates: {
          ...state.estimates,
          entities: updatedEntities,
        },
      }
    }

    case types.FETCH_REINSTATE_ESTIMATE_REQUEST:
      return {
        ...state,
        estimates: {
          ...state.estimates,
          busy: true,
          error: '',
        },
      }

    case types.FETCH_REINSTATE_ESTIMATE_SUCCESS:
      return {
        ...state,
        estimates: {
          ...state.estimates,
          busy: false,
          entities: {
            ...state.estimates.entities,
            reinstate: {
              ...state.estimates.entities.reinstate,
              [meta.uuid]: payload,
            },
          },
        },
      }

    case types.FETCH_REINSTATE_ESTIMATE_ERROR:
      return {
        ...state,
        estimates: {
          ...state.estimates,
          busy: false,
          error: payload.response.message,
        },
      }

    default:
      return state
  }
}
